interface LocalizedText {
  en: string;
  hi: string;
}

// Not exported: nothing outside this module needs the tone union directly,
// only the tiles that carry it (fallow's dead-code check flags an exported
// type with no importer). Mirrors resources.ts's private TileTone.
type TileTone = "teal" | "mint" | "sun" | "peach" | "sage" | "stone";

// Not exported: nothing outside this module needs the per-tile shape, only
// the assembled ACTIVITIES_TILES value below (fallow's dead-code check
// flags an exported type with no importer).
interface ActivityTile {
  title: LocalizedText;
  sub: LocalizedText;
  href: string;
  tone: TileTone;
}

/**
 * `/activities` (and `/activities-HI`): the four hub tiles, verbatim
 * titles and sub-labels from the old site's own carousel (its
 * `card-title`/`card-text` attributes) - note the hub calls the weekly
 * session tile "Weekly Rejuvenation", not "Weekly Session" (that's only
 * the nav menu's label for the same page). The old site's Hindi rendering
 * of this page carried the same English carousel text (no distinct Hindi
 * copy exists), so `hi` mirrors `en` for every tile here.
 */
export const ACTIVITIES_TILES: readonly ActivityTile[] = [
  {
    title: { en: "Daily Practice", hi: "Daily Practice" },
    sub: {
      en: "Meditation | Knowledge Sharing | Swarved",
      hi: "Meditation | Knowledge Sharing | Swarved",
    },
    href: "/activities/daily-practice",
    tone: "teal",
  },
  {
    title: { en: "Weekly Rejuvenation", hi: "Weekly Rejuvenation" },
    sub: {
      en: "Meditation | Panel Discussion | Q&A",
      hi: "Meditation | Panel Discussion | Q&A",
    },
    href: "/activities/weekly-session",
    tone: "mint",
  },
  {
    title: { en: "Quarterly Activity", hi: "Quarterly Activity" },
    sub: {
      en: "Swarved Recitation | Sankat Mochan",
      hi: "Swarved Recitation | Sankat Mochan",
    },
    href: "/activities/swarved-akhand-path",
    tone: "sun",
  },
  {
    title: { en: "Others", hi: "Others" },
    sub: {
      en: "International Day of Yoga | Gita Jayanti 2021",
      hi: "International Day of Yoga | Gita Jayanti 2021",
    },
    href: "/activities/gita-jayanti-2021",
    tone: "peach",
  },
] as const;

/**
 * The shared meditation Zoom room. Both of Daily Practice's meditation
 * cards and the Weekly Session page open this one room - and this single
 * constant is the fix for a defect on the old `/weekly-session` page: it
 * displayed a stale, mismatched ID ("89436021905") on a button that
 * actually opened `https://zoom.us/j/383310813`. Every consumer reads the
 * ID and href from here, so the displayed ID can never drift from the
 * link it sits next to again.
 */
export const ZOOM_ROOM = {
  id: "383310813",
  href: "https://zoom.us/j/383310813",
} as const;

/**
 * The two WhatsApp groups both Daily Practice and Weekly Session linked on
 * the old site (as WhatsApp icons, identical on both pages): the main "VY
 * Meditation" group and the "VY Meditation - Daily 2" group. `suffix`
 * distinguishes the second button; the base label stays the localised
 * "WhatsApp Group" from the dictionary.
 */
export const ACTIVITIES_WHATSAPP_GROUPS: readonly {
  href: string;
  suffix?: string;
}[] = [
  { href: "https://chat.whatsapp.com/GP4UvtKLPzu453AJm8ab9I" },
  {
    href: "https://chat.whatsapp.com/Fg0PRtfxlP1DYFW8tPoY41",
    suffix: "Daily 2",
  },
];

// Not exported: only the assembled DAILY_PRACTICE_CARDS value below needs
// the per-card shape (fallow's dead-code check flags an exported type
// with no importer).
interface DailyPracticeCard {
  title: LocalizedText;
  eyebrow: LocalizedText;
  weekday: LocalizedText;
  weekend: LocalizedText;
  ctaHref: string;
  external: boolean;
}

/**
 * `/daily-practice` (and `/daily-practice-HI`): the four schedule cards,
 * verbatim titles and Singapore-time schedules from the old site's
 * carousel. The two meditation cards open the shared `ZOOM_ROOM`;
 * Knowledge Sharing opens the slide-reading hub; Swarved Reading opens
 * `/swarved`. The old site's Hindi rendering of this page carried the
 * same English carousel text (no distinct Hindi copy exists), so `hi`
 * mirrors `en` for every field here.
 */
export const DAILY_PRACTICE_CARDS: readonly DailyPracticeCard[] = [
  {
    title: {
      en: "Morning Meditation Practice",
      hi: "Morning Meditation Practice",
    },
    eyebrow: { en: "Morning", hi: "Morning" },
    weekday: {
      en: "Mon-Fri: @6am, @7am Singapore Time",
      hi: "Mon-Fri: @6am, @7am Singapore Time",
    },
    weekend: {
      en: "Sat-Sun: @8am, @9am(kids) Singapore Time",
      hi: "Sat-Sun: @8am, @9am(kids) Singapore Time",
    },
    ctaHref: ZOOM_ROOM.href,
    external: true,
  },
  {
    title: {
      en: "Evening Meditation Practice",
      hi: "Evening Meditation Practice",
    },
    eyebrow: { en: "Evening", hi: "Evening" },
    weekday: {
      en: "Mon-Fri: @9pm Singapore Time",
      hi: "Mon-Fri: @9pm Singapore Time",
    },
    weekend: {
      en: "Sat-Sun: @9pm Singapore Time",
      hi: "Sat-Sun: @9pm Singapore Time",
    },
    ctaHref: ZOOM_ROOM.href,
    external: true,
  },
  {
    title: { en: "Knowledge Sharing", hi: "Knowledge Sharing" },
    eyebrow: { en: "Evening", hi: "Evening" },
    weekday: {
      en: "Mon-Fri: 9:30-9:40pm Singapore Time",
      hi: "Mon-Fri: 9:30-9:40pm Singapore Time",
    },
    weekend: { en: "Sat-Sun: Nil", hi: "Sat-Sun: Nil" },
    ctaHref: "/resources/slide-reading",
    external: false,
  },
  {
    title: { en: "Swarved Reading", hi: "Swarved Reading" },
    eyebrow: { en: "Morning", hi: "Morning" },
    weekday: {
      en: "Mon-Fri: @6:30am Singapore Time",
      hi: "Mon-Fri: @6:30am Singapore Time",
    },
    weekend: {
      en: "Sat-Sun: @8:30am Singapore Time",
      hi: "Sat-Sun: @8:30am Singapore Time",
    },
    ctaHref: "/swarved",
    external: false,
  },
] as const;

// Not exported: only the assembled WEEKLY_SESSION_DISCUSSIONS value below
// needs the per-item shape (fallow's dead-code check flags an exported
// type with no importer).
interface WeeklyDiscussion {
  title: string;
  basis: string;
  schedule: string;
}

/**
 * `/weekly-session`: the two Sunday discussion cards, verbatim titles,
 * basis line and schedule - both open `/activities/sessions` on the old
 * site, so that single href lives in `WEEKLY_SESSION_HREF` below rather
 * than being repeated per card. English only - the old site had no Hindi
 * rendering of this page.
 */
export const WEEKLY_SESSION_DISCUSSIONS: readonly WeeklyDiscussion[] = [
  {
    title: "Spiritual Discussion",
    basis: "Based on Swarved Chapters",
    schedule: "Sunday 4pm-5pm Singapore Time",
  },
  {
    title: "Vihangam Yoga Discussion on Practicality",
    basis: "Based on Swarved Chapters",
    schedule: "Sunday 4pm-5pm Singapore Time",
  },
] as const;

/** Where both Weekly Session discussion cards link, on the old site. */
export const WEEKLY_SESSION_SESSIONS_HREF = "/activities/sessions";

// Not exported: only the assembled SESSIONS_EN/SESSIONS_HI values below
// need the per-entry shape (fallow's dead-code check flags an exported
// type with no importer).
interface SessionEntry {
  title: string;
  date: string;
  blurb: string;
  href: string;
}

/**
 * `/sessions`: the "Past Sessions (English)" column, verbatim titles (the
 * date is split out of the old site's trailing " - <date>" suffix),
 * blurbs and Facebook/YouTube links, in the old carousel's order.
 */
export const SESSIONS_EN: readonly SessionEntry[] = [
  {
    title: "Are you Ready to Change your Habits (Part 2)",
    date: "2021 May 9",
    blurb:
      "(Part 2) Know how Vihangam Meditation helps to replace your habits permanently",
    href: "https://www.facebook.com/VihangamYogaSingapore/videos/868664523863720",
  },
  {
    title: "Are you Ready to Change your Habits (Part 1)",
    date: "2021 May 9",
    blurb:
      "Habits sustain due to rewards. Being aware about it, and then replacing unwanted habit with wanted one is the real solution.",
    href: "https://www.facebook.com/VihangamYogaSingapore/videos/598241731149724",
  },
  {
    title: "Tackling Difficult Situations",
    date: "2021 April 25",
    blurb:
      "Problems / Difficult situations are inevitable. How do you react, and then act in difficult situation?",
    href: "https://youtu.be/b5xaL4Kf3v4",
  },
  {
    title: "Are You Sleeping Well? (Part 2)",
    date: "2021 April 11",
    blurb:
      "How Vihangam Meditation can rectify your sleep, and enable you to add 1-hour to your daily life.",
    href: "https://fb.watch/4OAbyXtY66/",
  },
  {
    title: "Are You Sleeping Well? (Part 1)",
    date: "2021 April 11",
    blurb:
      "Do you drag yourself from the bed in the morning when you get up ? Do you have enough sleep? Can you sleep less to save your time?",
    href: "https://fb.watch/4OA8PGNqgI/",
  },
] as const;

/**
 * `/sessions`: the "Past Sessions (Hindi)" column, verbatim titles (the
 * old site renders these as a Devanagari title followed by an English
 * gloss in parentheses - kept exactly as shown, including the one entry,
 * "Swarved: An Introduction", whose own title on the old site has no
 * Devanagari at all), blurbs and Facebook links, in the old carousel's
 * order.
 */
export const SESSIONS_HI: readonly SessionEntry[] = [
  {
    title: "गुरु और सद्गुरु (Guru & Sadguru)",
    date: "2021 Jul 4",
    blurb:
      "Who is Guru, who is Sadguru? Are they really important to exist in our lives?",
    href: "https://www.facebook.com/701222476630154/videos/504206924204269",
  },
  {
    title: "मृत्यु के कगार पर (On the Verge of Death)",
    date: "2021 May 2",
    blurb:
      "We are here, in this life, with an objective to WIN over the DEATH. IS this really possible? How Vihangam Yoga is the only way to make you learn to achieve this is discussed.",
    href: "https://www.facebook.com/VihangamYogaSingapore/videos/1582840355239875",
  },
  {
    title: "Swarved: An Introduction",
    date: "2021 April 04",
    blurb:
      "What is Swarved? How is different and unique compared to other scripture? How can Swarved help you in your day-to-day life as well as on the path of spirituality?",
    href: "https://fb.watch/4PxESlXnqU/",
  },
] as const;

// Not exported: only the assembled AKHAND_PATH_RECORDINGS value below
// needs the per-recording shape (fallow's dead-code check flags an
// exported type with no importer).
interface AkhandPathRecording {
  date: string;
  href: string;
}

/**
 * `/swarved-24-hr-recitation` (redirected from as `/activities/swarved-akhand-path`):
 * the past Swarved Akhand Path recordings, verbatim dates and Facebook
 * links, newest first as the old carousel ordered them. The old site's
 * final entry, "(Upcoming) Swarved Akhand Path - 27 Jan 2024", is dropped
 * here - that date has passed, so the item is stale rather than upcoming.
 */
export const AKHAND_PATH_RECORDINGS: readonly AkhandPathRecording[] = [
  {
    date: "15 Oct 2022",
    href: "https://www.facebook.com/VihangamYogaSingapore/videos/511249961011292",
  },
  {
    date: "16 Jul 2022",
    href: "https://www.facebook.com/VihangamYogaSingapore/videos/566098935193594",
  },
  {
    date: "2 Apr 2022",
    href: "https://www.facebook.com/VihangamYogaSingapore/videos/653388802407938",
  },
  {
    date: "29 Jan 2022",
    href: "https://www.facebook.com/watch/live/?ref=watch_permalink&v=468048008159343",
  },
  {
    date: "22 Oct 2021",
    href: "https://www.facebook.com/VihangamYogaSingapore/videos/336105891621823",
  },
  {
    date: "2 Apr2021",
    href: "https://www.facebook.com/VihangamYogaSingapore/videos/467523001334345",
  },
  {
    date: "1 Jan 2021",
    href: "https://www.facebook.com/events/1109382509526625",
  },
  {
    date: "31 Oct 2020",
    href: "https://www.facebook.com/100001937248429/videos/4632690563472139/",
  },
] as const;

/** The "Know more about Swarved" button on the Akhand Path page. */
export const AKHAND_PATH_SWARVED_HREF = "/swarved";

/**
 * The "I Want to Recite" mailto on the Akhand Path page, verbatim subject
 * line from the old site's own button.
 */
export const AKHAND_PATH_RECITE_MAILTO =
  "mailto:vysingapore@gmail.com?Subject=I want to Recite Swarved!";

/**
 * `/gita-jayanti-2021`: the old page is just a two-line heading ("Gita
 * Jayanti 2021" / "Singapore") and a "click to know more >>" link, all
 * three pointing at the same external event site. English only - no
 * Hindi twin.
 */
export const GITA_JAYANTI = {
  title: "Gita Jayanti 2021",
  location: "Singapore",
  href: "https://www.gitajayanti.org.sg/",
} as const;
